'use client';

import {
  MaximumFractionDigits,
  getDecimalsByDisplayType,
} from '@bts-web/utils-formatting';
import { useClientLocale } from '@bts-web/utils-lokalise';
import { css } from '@bts-web/utils-style-engine';
import { AmountFor } from '@bts-web/data-layer/server';
import { FC, ReactNode, useEffect, useRef, useState } from 'react';
import {
  amountInputWrapperStyles,
  getAssetDisplayValueFromSource,
} from '../../../common';
import { TradeControllerVariants, TradeOperation } from '../../../trade/utils';
import { RegularPen } from '../../svgIcons';
import { getInputStyleByAmountLength } from './getInputStyleByAmountLength';

const CHARS_BEFORE_ELLIPSIS = 11;

export type AmountInputWrapperProps = {
  title?: string;
  switcherElementSlot?: ReactNode;
  learnMoreElementSlot?: ReactNode;
  inputContextData: {
    displayType: AmountFor;
    value: string;
    currency: string;
    symbol: string;
    transactionType?: TradeControllerVariants;
    placeholder?: string;
  };
  onInputBlur?: (value: string) => void;
  children?: (props: {
    inputRef: React.RefObject<HTMLInputElement | null>;
    modifiedBlur: (value: string) => void;
  }) => ReactNode;
};

export const AmountInputWrapper: FC<AmountInputWrapperProps> = ({
  switcherElementSlot,
  learnMoreElementSlot,
  children,
  onInputBlur,
  inputContextData,
  title,
}) => {
  const locale = useClientLocale();

  const { value, placeholder } = inputContextData;

  const showPlaceHolder = value.length === 0;

  const displayedValue =
    value !== '' && Number.isFinite(Number(value))
      ? Number(value).toFixed(
          getDecimalsByDisplayType(inputContextData.displayType),
        )
      : value;

  const fontSizeProperty = getInputStyleByAmountLength(
    displayedValue,
    false,
    inputContextData?.currency,
  );

  const [fillerDisplayValue, setFillerDisplayValue] = useState<string>('');

  const [isFocused, setIsFocused] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);

  const modifiedBlur = (value: string) => {
    setIsFocused(false);

    if (onInputBlur) onInputBlur(value);
  };

  const onInputRegionFocus = () => {
    setIsFocused(true);

    setTimeout(() => {
      inputRef.current?.focus();
    }, 50);
  };

  useEffect(() => {
    const { currency, displayType, value, symbol, transactionType } =
      inputContextData;

    setFillerDisplayValue(
      getAssetDisplayValueFromSource({
        currency: currency,
        locale,
        sourceAmount: value,
        sourceType: displayType,
        customSymbol: symbol,
        ...(transactionType === TradeOperation.swap
          ? { customMaximumFractionDigits: MaximumFractionDigits.ASSET }
          : {}),
      }),
    );
  }, [inputContextData]);

  useEffect(() => {
    setTimeout(() => {
      if (children) {
        onInputRegionFocus();
      }
    }, 300);
  }, []);

  const displayStyles = {
    ...fontSizeProperty,
    ...(value.length >= CHARS_BEFORE_ELLIPSIS && {
      textOverflow: 'ellipsis',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      maxWidth: '100%',
    }),
  };

  return (
    <>
      {title ? (
        <div
          className={css({
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingBottom: 'extra_small',
          })}
        >
          <div
            className={css({
              fontSize: 'title.large_medium',
              fontWeight: 'body.medium_medium',
              letterSpacing: 'title.large_medium',
              lineHeight: 'title.large_medium',
            })}
          >
            {title}
          </div>
          {learnMoreElementSlot}
        </div>
      ) : null}

      <div
        data-testid="amount-input-wrapper"
        className={css({
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          width: '100%',
        })}
      >
        <div
          className={css({
            flex: 1,
            minWidth: 0,
            minHeight: 46,
          })}
        >
          {!isFocused && (
            <div className={amountInputWrapperStyles}>
              <div
                aria-label="input trade amount"
                data-testid="trade-summary-input-button"
                onClick={children ? onInputRegionFocus : undefined}
                className={`cursorPointer inputHeight ${showPlaceHolder ? 'placeHolderFiller' : ''}`}
                style={displayStyles}
              >
                <span
                  className={css({
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    width: '100%',
                    display: 'block',
                  })}
                >
                  {showPlaceHolder ? placeholder : fillerDisplayValue}
                </span>
                {showPlaceHolder && (
                  <RegularPen
                    size={40}
                    className={css({
                      position: 'absolute',
                      right: 'extra_small_2',
                      _rtl: {
                        right: 'initial',
                        left: 'extra_small_2',
                      },
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: 'neutrals.fill_quaternary',
                    })}
                  />
                )}
              </div>
            </div>
          )}

          {isFocused && (
            <div>
              {children && inputRef !== null
                ? children({ inputRef, modifiedBlur })
                : null}
            </div>
          )}
        </div>

        {switcherElementSlot && (
          <div
            className={css({
              flex: 'none',
              paddingInlineStart: 'extra_small_2',
            })}
          >
            {switcherElementSlot}
          </div>
        )}
      </div>
    </>
  );
};
